import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import type { Filters, LeadListPage, LeadRow, LeadSlice } from "../lib/api.ts";
import { money, num } from "../lib/format.ts";
import { DrawerActions } from "./exportButton.tsx";
import { exportToExcel, fetchAllPages, moneyValue, type ExportColumn } from "../lib/tableExport.ts";
import { dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { CHANNEL, UI } from "../i18n/dict.ts";
import { Th, useSortable, type SortColumn } from "./sortable.tsx";

/**
 * Список лидов под цифрой, с переходом в карточку Битрикса.
 *
 * Панель открывается поверх экрана, а не уводит на отдельную страницу: человек
 * проверяет цифру, а не уходит работать со списком. Уход со страницы потерял бы
 * фильтры и период, к которым цифра относится, и вернуться к ней было бы нечем.
 *
 * Один компонент на все экраны сознательно. Список, написанный отдельно под
 * каждую плитку, разошёлся бы с плиткой — это тот самый класс дефекта, из-за
 * которого одно понятие давало на четырёх экранах четыре разных числа.
 */

/** Проза этой панели. Один блок в начале файла — см. App.tsx. */
const TX = {
  restoredByAnalytics: {
    ru: "восстановлено аналитикой, а не измерено CRM",
    en: "restored by analytics, not measured by the CRM",
  },
  /* Счётное существительное: в русском три формы. */
  leadsCount: {
    one: { ru: "лид", en: "lead" },
    few: { ru: "лида", en: "leads" },
    many: { ru: "лидов", en: "leads" },
  },
  noMatch: { ru: "Под этот срез лидов нет.", en: "No leads match this slice." },
  colCreated: { ru: "Создан", en: "Created" },
  colOwner: { ru: "Ответственный", en: "Owner" },
  colAmount: { ru: "Сумма", en: "Amount" },
  partyOn: { ru: "праздник", en: "party" },
  /* --- колонки выгрузки в Excel: вторая строка ячейки — отдельной колонкой */
  exLeadId: { ru: "ID лида", en: "Lead ID" },
  exPhone: { ru: "Телефон", en: "Phone" },
  exBasis: { ru: "Откуда источник", en: "Where the source comes from" },
  exTag: { ru: "Метка", en: "Tag" },
  exDealId: { ru: "ID сделки", en: "Deal ID" },
  exParty: { ru: "Праздник", en: "Party" },
  exLeadLink: { ru: "Ссылка на лид", en: "Lead link" },
  exDealLink: { ru: "Ссылка на сделку", en: "Deal link" },
  previousPage: { ru: "← Назад", en: "← Previous" },
  nextPage: { ru: "Далее →", en: "Next →" },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/**
 * Происхождение метки, коротко. Ключ — `basis` из ответа сервера.
 *
 * Формулировки СВОИ, а не из общего словаря `UTM_BASIS`: там подписи длинные,
 * они объясняют происхождение целой фразой и стоят под столбиками охвата, а
 * здесь это значок в строке таблицы, и он обязан быть в два слова.
 */
const BASIS_LABEL: Record<string, L> = {
  client_tag: { ru: "метка клиента", en: "client tag" },
  analytics_tag: { ru: "наша метка", en: "our tag" },
  chat_link: { ru: "ссылка из чата", en: "chat link" },
  source_field: { ru: "поле источника", en: "source field" },
  site_ref: { ru: "сайт бренда", en: "brand website" },
};

/**
 * Почему происхождение метки подписано у каждой строки.
 *
 * У 492 лидов метку Meta проставили мы сами, разобрав ссылку из первого
 * сообщения чата. Это законный вывод, но не измерение заказчика, и стоит их
 * смешать — оговорка про цену лида перестанет быть правдой. Поэтому метка
 * подписана всегда, а не только когда кто-то спросит.
 */
function Basis({ basis }: { basis: string }): React.ReactElement {
  const ours = basis === "analytics_tag" || basis === "chat_link";
  return (
    <span className={ours ? "tag tag-warn" : "tag"} title={ours ? t(TX.restoredByAnalytics) : undefined}>
      {dbLabel(BASIS_LABEL, basis, basis)}
    </span>
  );
}

/**
 * Колонки выгрузки в Excel — те же, что у таблицы, в том же порядке; телефон,
 * происхождение метки и дата праздника из второй строки ячейки — рядом
 * отдельными колонками.
 */
function leadExportColumns(): Array<ExportColumn<LeadRow>> {
  return [
    { title: t(UI.lead), width: 34, value: (r) => (r.title === null || r.title === "" ? `#${r.lead_id}` : r.title) },
    { title: t(TX.exLeadId), kind: "number", width: 10, value: (r) => r.lead_id },
    { title: t(TX.exPhone), width: 16, value: (r) => r.phone_e164 },
    { title: t(TX.colCreated), kind: "date", width: 12, value: (r) => r.created },
    { title: t(UI.source), width: 24, value: (r) => dbLabel(CHANNEL, r.source_group, r.source_name) },
    { title: t(TX.exBasis), width: 18, value: (r) => dbLabel(BASIS_LABEL, r.basis, r.basis) },
    {
      title: t(TX.exTag),
      width: 22,
      value: (r) => [r.tag_source, r.tag_medium].filter((x) => x !== null && x !== "").join(" / "),
    },
    { title: t(UI.park), width: 24, value: (r) => r.park_name },
    { title: t(UI.stage), width: 20, value: (r) => r.stage_name },
    { title: t(TX.colOwner), width: 22, value: (r) => r.manager_name },
    { title: t(UI.deal), width: 20, value: (r) => (r.deal_id === null ? null : (r.deal_stage_name ?? `#${r.deal_id}`)) },
    { title: t(TX.exDealId), kind: "number", width: 10, value: (r) => r.deal_id },
    { title: t(TX.exParty), kind: "date", width: 12, value: (r) => r.date_of_event },
    { title: t(TX.colAmount), kind: "money", width: 12, value: (r) => moneyValue(r.deal_amount_aed_minor) },
    { title: t(TX.exLeadLink), width: 50, value: (r) => r.bitrix_url },
    { title: t(TX.exDealLink), width: 50, value: (r) => r.deal_bitrix_url },
  ];
}

/**
 * Колонки списка. На уровне модуля, потому что доступ должен быть виден хуку,
 * который вызывается до раннего выхода компонента.
 *
 * Три колонки повторяют формулу ячейки, а не берут поле: подпись лида
 * подставляет `#id` вместо пустого заголовка, сделка печатает стадию либо
 * `#id`, а её отсутствие — прочерк. Прочерк и любой другой заменитель обязаны
 * дать `null`, иначе лид без сделки встанет между настоящими значениями.
 */
const LEAD_COLS: Record<
  "lead" | "created" | "source" | "park" | "stage" | "owner" | "deal" | "amount",
  SortColumn<LeadRow>
> = {
  lead: { kind: "text", get: (r) => (r.title === null || r.title === "" ? `#${r.lead_id}` : r.title) },
  /* Дата приходит из Постгреса строкой `YYYY-MM-DD` — текстовая колонка её и
     упорядочивает верно, сегменты выровнены по позициям. */
  created: { kind: "text", get: (r) => r.created },
  source: { kind: "text", get: (r) => dbLabel(CHANNEL, r.source_group, r.source_name) },
  park: { kind: "text", get: (r) => r.park_name },
  stage: { kind: "text", get: (r) => r.stage_name },
  owner: { kind: "text", get: (r) => r.manager_name },
  deal: { kind: "text", get: (r) => (r.deal_id === null ? null : (r.deal_stage_name ?? `#${r.deal_id}`)) },
  /* Минорные единицы, а не то, что напечатала `money()`: «436К» и «91 200» как
     строки несравнимы. */
  amount: { kind: "number", get: (r) => r.deal_amount_aed_minor },
};

export type DrillDown = {
  title: string;
  /** Пояснение под заголовком: по какому именно условию собран список. */
  hint?: string;
  slice: LeadSlice;
  /**
   * Своё окно вместо периода с верхней панели.
   *
   * Нужно тем цифрам, которые период НЕ применяют. На утреннем дайджесте таких
   * две: «Воронка стареет» отвечает на вопрос «что лежит в воронке сейчас» и
   * безразлична к периоду вовсе, а «метки без правила» считают свои 30 дней и
   * все парки сразу. Без этого поля под числом 6 открылось бы четыре строки —
   * ровно тот дефект, от которого детализация и существует.
   *
   * Поля накладываются поверх текущих фильтров, а не заменяют их целиком.
   */
  filters?: Partial<Filters>;
};

export function LeadDrawer({
  drill,
  filters,
  onClose,
}: {
  drill: DrillDown | null;
  filters: Filters;
  onClose: () => void;
}): React.ReactElement | null {
  const [page, setPage] = useState<LeadListPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const key = drill === null ? "" : JSON.stringify([drill.slice, filters]);

  /** Смена среза возвращает на первую страницу: иначе откроется пустой хвост. */
  useEffect(() => {
    setOffset(0);
  }, [key]);

  useEffect(() => {
    if (drill === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .leads(filters, drill.slice, 100, offset)
      .then((r) => {
        if (alive) setPage(r);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [key, offset]);

  /** Escape закрывает — панель модальная, и мышь до крестика тянуть не нужно. */
  useEffect(() => {
    if (drill === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill, onClose]);

  const rows = page?.rows ?? [];
  const total = page?.total ?? 0;

  /**
   * Сортировка включается ТОЛЬКО когда на экране весь срез целиком.
   *
   * Список страничный: сервер отдаёт по 100 строк с `offset` и сортирует их сам
   * по дате создания (`ORDER BY p.date_create DESC`), а срез бывает в тысячи
   * лидов. Отсортировать страницу по сумме значило бы напечатать сверху «самую
   * крупную сделку» из ста случайных — не неудобство, а неверное число в
   * отчёте. Своего параметра сортировки у `/api/leads` нет, и выдумывать его
   * здесь нельзя: это чужой файл.
   *
   * Поэтому `partial` вычисляется по данным: пока весь срез уместился на одной
   * странице, сортировать честно; как только страниц больше одной — заголовки
   * перестают быть кнопками и объясняют причину подсказкой. Условие ровно то
   * же, по которому ниже появляется листалка, так что «видна листалка —
   * сортировки нет» читается без объяснений.
   */
  const S = useSortable(rows, LEAD_COLS, { partial: total > rows.length });

  if (drill === null) return null;

  const shown = offset + rows.length;

  /* Весь список под цифрой: если он уже целиком на экране — в том порядке, как
     отсортирован; иначе со всех страниц. */
  const runExport = async (progress: (done: number, total: number) => void): Promise<void> => {
    const all =
      total <= rows.length
        ? S.rows
        : await fetchAllPages((limit, off) => api.leads(filters, drill.slice, limit, off), progress);
    await exportToExcel(drill.title, leadExportColumns(), all);
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <div className="drawer-title">{drill.title}</div>
            <div className="drawer-hint">
              {loading && page === null
                ? t(UI.loading)
                : `${num(total)} ${plural(total, TX.leadsCount)}`}
              {drill.hint ? ` · ${drill.hint}` : ""}
            </div>
          </div>
          <DrawerActions onClose={onClose} exportRun={runExport} exportDisabled={loading || total === 0} />
        </div>

        {/* Содержимое отделено от липкой шапки: панель выезжает справа во весь
            рост экрана, и шапка должна оставаться на месте при прокрутке. */}
        <div className="drawer-body">
        {error !== null && <div className="error">{error}</div>}

        {error === null && rows.length === 0 && !loading && (
          <div className="empty">{t(TX.noMatch)}</div>
        )}

        {rows.length > 0 && (
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={S} col="lead">{t(UI.lead)}</Th>
                  <Th sort={S} col="created">{t(TX.colCreated)}</Th>
                  <Th sort={S} col="source">{t(UI.source)}</Th>
                  <Th sort={S} col="park">{t(UI.park)}</Th>
                  <Th sort={S} col="stage">{t(UI.stage)}</Th>
                  <Th sort={S} col="owner">{t(TX.colOwner)}</Th>
                  <Th sort={S} col="deal">{t(UI.deal)}</Th>
                  <Th sort={S} col="amount" num>{t(TX.colAmount)}</Th>
                  {/* Кнопка перехода в Битрикс — сортировать нечего. */}
                  <th />
                </tr>
              </thead>
              <tbody>
                {S.rows.map((r) => (
                  <tr key={r.lead_id}>
                    <td>
                      <a href={r.bitrix_url} target="_blank" rel="noreferrer">
                        {r.title === null || r.title === "" ? `#${r.lead_id}` : r.title}
                      </a>
                      {r.phone_e164 !== null && <div className="sub">{r.phone_e164}</div>}
                    </td>
                    <td className="nowrap">{r.created}</td>
                    <td>
                      {dbLabel(CHANNEL, r.source_group, r.source_name)}
                      <div className="sub">
                        <Basis basis={r.basis} />
                        {r.tag_source !== null && ` ${r.tag_source}`}
                        {r.tag_medium !== null && ` / ${r.tag_medium}`}
                      </div>
                    </td>
                    <td>{r.park_name ?? "—"}</td>
                    <td>{r.stage_name ?? "—"}</td>
                    <td>{r.manager_name ?? "—"}</td>
                    <td>
                      {r.deal_id === null ? (
                        "—"
                      ) : (
                        <a href={r.deal_bitrix_url ?? "#"} target="_blank" rel="noreferrer">
                          {r.deal_stage_name ?? `#${r.deal_id}`}
                        </a>
                      )}
                      {r.date_of_event !== null && (
                        <div className="sub">
                          {t(TX.partyOn)} {r.date_of_event}
                        </div>
                      )}
                    </td>
                    <td className="num">
                      {r.deal_amount_aed_minor === null
                        ? "—"
                        : money(r.deal_amount_aed_minor, "AED", { withCode: false })}
                    </td>
                    <td className="nowrap">
                      <a className="btn-ghost" href={r.bitrix_url} target="_blank" rel="noreferrer">
                        Bitrix ↗
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > rows.length && (
          <div className="drawer-foot">
            <button
              className="btn-ghost"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - 100))}
            >
              {t(TX.previousPage)}
            </button>
            <span className="sub">
              {num(offset + 1)}–{num(shown)} {t(UI.of)} {num(total)}
            </span>
            <button
              className="btn-ghost"
              disabled={shown >= total || loading}
              onClick={() => setOffset(offset + 100)}
            >
              {t(TX.nextPage)}
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
